import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Clock, CheckCircle2, AlertCircle, X, Loader2, FileSpreadsheet, FileText } from 'lucide-react';

const JobStatusTable = ({ jobs, onDownload, onCancel }) => {
  const { t } = useTranslation();

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusInfo = {
    pending: {
      icon: <Clock size={16} className="text-amber-500" />,
      text: t('report.pending'),
      className: 'bg-amber-50 text-amber-700',
    },
    processing: {
      icon: <Loader2 size={16} className="text-blue-500 animate-spin" />,
      text: t('report.processing'),
      className: 'bg-blue-50 text-blue-700',
    },
    completed: {
      icon: <CheckCircle2 size={16} className="text-emerald-500" />,
      text: t('report.completed'),
      className: 'bg-emerald-50 text-emerald-700',
    },
    failed: {
      icon: <AlertCircle size={16} className="text-red-500" />,
      text: t('report.failed'),
      className: 'bg-red-50 text-red-700',
    },
    default: {
      icon: <Clock size={16} className="text-gray-400" />,
      text: t('report.unknown'),
      className: 'bg-gray-50 text-gray-500',
    },
  };

  const getStatusDisplay = (status) => {
    const info = statusInfo[status] || statusInfo.default;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold gap-2 ${info.className}`}>
        {info.icon}
        <span className="uppercase tracking-wider">{info.text}</span>
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes == null) return null;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return `0 B`;
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (format, status) => {
    let colorClass = 'text-gray-500';
    if (status === 'completed') {
      colorClass = 'text-green-500';
    } else if (status === 'failed') {
      colorClass = 'text-red-500';
    } else if (status === 'pending' || status === 'processing') {
      colorClass = 'text-blue-500';
    }

    if (format === 'excel' || format === 'csv') return <FileSpreadsheet size={20} className={colorClass} />;
    return <FileText size={20} className={colorClass} />;
  };

  if (!jobs || jobs.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {jobs.map((job) => (
        <div
          key={job.id}
          className="
                group relative bg-white rounded-2xl p-3 sm:p-4 
                border border-gray-200 
                shadow-sm hover:shadow-md
                transition-shadow duration-300 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3
              "
        >
          <div className="flex items-start gap-4 w-full sm:w-auto">
            {/* File Icon */}
            <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105">
              {getFileIcon(job.format, job.status)}
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <span className="text-base font-bold text-gray-900">
                  {t(`report.${job.report_type}Report`) || 'Report'}
                </span>
                {getStatusDisplay(job.status)}
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                <span>ID: {job.id.substring(0, 8)}</span>
                <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                <span>Created: {formatDate(job.created_at)}</span>
                {job.status === 'completed' && job.completed_at && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                    <span>Completed: {formatDate(job.completed_at)}</span>
                  </>
                )}
                {formatFileSize(job.file_size) && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                    <span>{formatFileSize(job.file_size)}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            {job.status === 'completed' && (
              <button
                onClick={() => onDownload(job.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white shadow-lg shadow-gray-200 transition-transform hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
              >
                <Download size={16} />
                <span>{t('report.download')}</span>
              </button>
            )}
            {(job.status === 'pending' || job.status === 'processing') && (
              <button
                onClick={() => onCancel(job.id)}
                className="p-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-transform hover:scale-105"
                title={t('report.cancelJobTitle')}
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default JobStatusTable;